import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { isDevCertModeActive, getDevCertUser } from '../auth/devCertAuthFixture';

/**
 * Robustly wait for the Firebase Auth state to resolve with a timeout, eliminating race conditions on boot.
 */
export const waitForAuth = (timeoutMs: number = 1500): Promise<User | null> => {
    return new Promise((resolve) => {
        if (isDevCertModeActive()) {
            const certUser = getDevCertUser();
            if (certUser) {
                return resolve(certUser as any);
            }
        }
        try {
            const auth = getAuth();
            if (auth.currentUser) {
                return resolve(auth.currentUser);
            }
            const timer = setTimeout(() => {
                resolve(auth.currentUser || null);
            }, timeoutMs);
            const unsubscribe = onAuthStateChanged(auth, (user) => {
                clearTimeout(timer);
                unsubscribe();
                resolve(user);
            });
        } catch (e) {
            resolve(null);
        }
    });
};

/**
 * Returns the current Firebase ID token (or mock fallback). Used to authenticate the
 * browser→backend voice relay connection (06V1-C). No Azure credential involved.
 */
export const getWingmanIdToken = async (): Promise<string | null> => {
    try {
        const user = await waitForAuth(1500);
        if (user) {
            return await user.getIdToken();
        }
        return 'mock-verified';
    } catch (e) {
        console.warn('[getWingmanIdToken] Could not retrieve Firebase auth token, using fallback', e);
        return 'mock-verified';
    }
};

/**
 * Returns the current Firebase ID token specifically for Avatar Gateway authentication (WINGMAN-AVATAR-002-H24-R6).
 * Reuses the existing Firebase authentication authority (waitForAuth -> user.getIdToken()).
 * Fails closed (returns null) when unauthenticated, ensuring no anonymous/mock dial is attempted.
 */
export const getWingmanAvatarToken = async (): Promise<string | null> => {
    try {
        const user = await waitForAuth(1500);
        if (user) {
            return await user.getIdToken();
        }
        return null;
    } catch (e) {
        return null;
    }
};

/**
 * A central utility wrapper around `fetch` that automatically injects
 * the Firebase Authorization header (Bearer token) for protected routes.
 * 
 * If a user is not logged in, it proceeds without the header and allows
 * the backend `validateAuth` middleware to correctly return a 401.
 */
export const authenticatedFetch = async (url: string | URL | Request, options: RequestInit = {}): Promise<Response> => {
    let token: string | null = null;
    let authUser: User | null = null;
    try {
        authUser = await waitForAuth();
        if (authUser) {
            // Retrieve cached token (Firebase automatically manages expiration internally)
            token = await authUser.getIdToken();
        }
    } catch (e) {
        console.warn('[authenticatedFetch] Could not retrieve Firebase auth token', e);
    }

    const headers = new Headers(options.headers || {});
    
    // Automatically inject Bearer token if we have one, and it hasn't been manually overridden
    if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    // Pass the new headers into the fetch options
    const newOptions: RequestInit = {
        ...options,
        headers
    };

    let response = await fetch(url, newOptions);

    // Bounded retry for expired tokens through the established refresh mechanism
    if ((response.status === 401 || response.status === 403) && authUser) {
        try {
            const freshToken = await authUser.getIdToken(true);
            if (freshToken && freshToken !== token) {
                const retryHeaders = new Headers(options.headers || {});
                retryHeaders.set('Authorization', `Bearer ${freshToken}`);
                response = await fetch(url, {
                    ...options,
                    headers: retryHeaders
                });
            }
        } catch {
            // Refresh failed; response remains original 401/403 without logging secrets
        }
    }

    return response;
};
